/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2022] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import NProgress from 'nprogress';
import Backbone from 'backbone';
import _ from 'lodash';

import Scaffold from '@/Scaffold';
import * as NavUtils from '@/util/NavUtils';
import { getUserDetails, isAuthorized, userHasRole } from '@/util/RoleUtils';
import ErrorView from '@/views/ErrorView';
import OrderTypes from '@/enums/OrderTypes';
import { formatDisplayDate } from '@/util/MomentUtils';

import GetAllLUOrderLineItems from '../services/GetAllLUOrderLineItems';
import GetOFMFDRelocationByOrderId from '../services/GetOFMFDRelocationByOrderId';
import GetDeliveryScheduleInfoForMFDRelocationForm from '../services/GetDeliveryScheduleInfoForMFDRelocationForm';
import OrderPrintPage from '../pages/OrderPrintPage';
import GetCustomerOrderById from '../services/GetCustomerOrderById';
import GetCustomerOrderDetails from '../services/GetCustomerOrderDetails';
import GetInternalPOCsByOrderId from '../services/GetInternalPOCsByOrderId';
import GetAllOrderStatus from '../../common/services/GetAllOrderStatus';
import LoadOrderGeneral from '../loaders/OrderGeneralLoader';
import LoadOrderSummary from '../loaders/OrderSummaryLoader';
import LoadOrderInformation from '../loaders/OrderInformationLoader';
import LoadOrderDelivery from '../loaders/OrderDeliveryLoader';
import OrderDevicesLoader from '../loaders/OrderDevicesLoader';
import DeliverySchedulePrintView from '../../common/DeliverySchedulePrintView/DeliverySchedulePrintView';

export default async function ShowOrderPrintPage(orderId) {
  NProgress.start();

  try {
    if (!isAuthorized() && EXTERNAL) {
      showAuthError();
      return;
    }

    const [allLineItems, orderDetails, extOrderDetails, internalPOCs, statuses] = await Promise.all([
      GetAllLUOrderLineItems(),
      GetCustomerOrderById(orderId),
      GetCustomerOrderDetails(orderId),
      GetInternalPOCsByOrderId(orderId),
      GetAllOrderStatus()
    ]);

    const orderFormTypeId = orderDetails.orderFormTypeId;
    orderDetails.orderTypeId = orderFormTypeId;
    const isRelocation = orderFormTypeId === OrderTypes.RELOCATION ? true : false;
    const isSpecialtyRequirement = orderFormTypeId === OrderTypes.SPECIALTY_REQ;

    const isCRSOwner = getUserDetails().id === orderDetails?.crsOwner;
    const isEPCOwner = getUserDetails().id === orderDetails?.epcOwner;
    const isInternalUser = userHasRole('CRS') || userHasRole('EPC');
    const statusId = orderDetails.statusId;
    orderDetails.status = getOrderStatusName(statuses, orderDetails, extOrderDetails);

    const orderData = { orderId, orderFormTypeId, isReadOnly: true, orderDetails, orderTypeId: orderFormTypeId };

    const [deviceView, generalView, deliveryView, orderSummaryView, informationView] = await Promise.all([
      isRelocation ? OrderDevicesLoader({ ...orderData, isReadOnly: true }) : Promise.resolve(null),
      LoadOrderGeneral({ editable: false, ...orderData }),
      LoadOrderDelivery({ ...orderData, isReadOnly: true }),
      LoadOrderSummary(orderData),
      LoadOrderInformation({ ...orderDetails, editable: false })
    ]);

    const deliveryScheduleView = new DeliverySchedulePrintView({
      model: new Backbone.Model({
        deliverySchedules: deliveryView.model.attributes.schedules,
        isRelocation,
        isSpecialtyRequirement
      })
    });

    navigateToPrintPage(orderId);
    if (isRelocation) {
      const formData = await getRelocationFormData(orderDetails, extOrderDetails);
      Scaffold.getRegion('content').show(
        new OrderPrintPage({
          model: new Backbone.Model({
            orderId,
            allLineItems,
            orderDetails,
            extOrderDetails,
            internalPOCs,
            order: formData.order,
            formattedSchedules: formData.formattedSchedules,
            isCRSOwner,
            isEPCOwner,
            isInternalUser,
            statusId,
            isRelocation
          })
        })
      );
    } else {
      Scaffold.getRegion('content').show(
        new OrderPrintPage({
          model: new Backbone.Model({
            orderId,
            orderDetails,
            extOrderDetails,
            internalPOCs,
            isCRSOwner,
            isEPCOwner,
            isInternalUser,
            statusId,
            generalView,
            deliveryView: deliveryScheduleView,
            orderSummaryView,
            informationView,
            deviceView,
            showPrintWindow: true,
            isRelocation,
            isSpecialtyRequirement
          })
        })
      );
    }
  } catch (err) {
    Scaffold.getRegion('content').show(
      new ErrorView({
        model: new Backbone.Model({
          errorMessage: _.isString(err.message) ? err.message : 'An Error Occurred',
          errorDetails: 'Something went wrong while loading the Print Order page.'
        })
      })
    );
  } finally {
    NProgress.done();
  }
}

function showAuthError() {
  Scaffold.getRegion('content').show(
    new ErrorView({
      model: new Backbone.Model({
        errorMessage: 'Not Authorized',
        errorDetails: 'You are not yet an authorized user. Please contact an administrator.'
      })
    })
  );
  NProgress.done();
}

async function getRelocationFormData(orderDetails) {
  const [order, schedules] = await Promise.all([
    GetOFMFDRelocationByOrderId(orderDetails.orderId),
    GetDeliveryScheduleInfoForMFDRelocationForm(orderDetails)
  ]);
  const formattedSchedules = formatRelocationSchedules(schedules[0]);
  if (orderDetails.submittedOn) orderDetails.submittedOn = formatDisplayDate(orderDetails.submittedOn);
  if (order.relocationDate) order.relocationDate = formatDisplayDate(order.relocationDate);
  return { order, formattedSchedules };
}

function formatRelocationSchedules(schedules) {
  const deliverySchedules = {
    fromDevices: formatRelocationFromSchedule(schedules),
    toDevices: formatRelocationToSchedule(schedules),
    equipmentName: schedules?.deviceModel,
    equipmentMake: schedules?.deviceMake,
    serialNumber: schedules?.serialNumber
  };
  return deliverySchedules;
}

function formatRelocationFromSchedule(schedule) {
  // schedule.equipmentName = schedule?.deviceMake?.concat(' ', schedule?.deviceModel);
  const fromDevice = {};

  fromDevice.deliveryPOCName = schedule.fromDeliveryPOCName;
  fromDevice.deliveryPOCFirstName = schedule.fromDeliveryPOC?.deliveryPOCFirstName;
  fromDevice.deliveryPOCLastName = schedule.fromDeliveryPOC?.deliveryPOCLastName;
  fromDevice.deliveryPOCEmail = schedule.fromDeliveryPOC?.deliveryPOCEmail;
  fromDevice.deliveryPOCPhone = schedule.fromDeliveryPOC?.deliveryPOCPhone;

  fromDevice.itPOCName = schedule.fromITPOCName;
  fromDevice.itPOCFirstName = schedule.fromDeliveryPOC?.itPOCFirstName;
  fromDevice.itPOCLastName = schedule.fromDeliveryPOC?.itPOCLastName;
  fromDevice.itPOCEmail = schedule.fromITPOC?.itPOCEmail;
  fromDevice.itPOCPhone = schedule.fromITPOC?.itPOCPhone;

  fromDevice.organization = schedule.fromOrgUnit;
  fromDevice.base = schedule.fromBase;

  fromDevice.address = schedule.fromAddress;

  return fromDevice;
}

function formatRelocationToSchedule(schedule) {
  const toDevice = {};

  toDevice.deliveryPOCName = schedule.toDeliveryPOCName;
  toDevice.deliveryPOCFirstName = schedule.toDeliveryPOC?.deliveryPOCFirstName;
  toDevice.deliveryPOCLastName = schedule.toDeliveryPOC?.deliveryPOCLastName;
  toDevice.deliveryPOCEmail = schedule.toDeliveryPOC?.deliveryPOCEmail;
  toDevice.deliveryPOCPhone = schedule.toDeliveryPOC?.deliveryPOCPhone;

  toDevice.itPOCName = schedule.toITPOCName;
  toDevice.itPOCFirstName = schedule.toITPOC?.itPOCFirstName;
  toDevice.itPOCLastName = schedule.toITPOC?.itPOCLastName;
  toDevice.itPOCEmail = schedule.toITPOC?.itPOCEmail;
  toDevice.itPOCPhone = schedule.toITPOC?.itPOCPhone;

  toDevice.organization = schedule.toOrgUnit;
  toDevice.base = schedule.toBase;

  toDevice.address = schedule.toAddress;

  return toDevice;
}

function getOrderStatusName(statuses, orderDetails, extOrderDetails) {
  let status;
  if (EXTERNAL) {
    _.each(statuses, (s) => {
      if (s.id === extOrderDetails.statusId) status = s.externalOrderStatus;
    });
  } else {
    _.each(statuses, (s) => {
      if (s.id === orderDetails.statusId) status = s.internalOrderStatus;
    });
  }
  return status;
}

function navigateToPrintPage(orderId) {
  if (EXTERNAL) {
    NavUtils.navigate(`ext/order/${orderId}/print`);
  } else {
    NavUtils.navigate(`order/${orderId}/print`);
  }
}
